import { Form } from 'antd';
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useSearchParams } from 'react-router-dom';

import { useGetEmployeesRecordsOnDatesMutation } from '@app/api/Records/api';
import { useLazyGetBranchEmployeesQuery } from '@app/api/Users';
import {
  useDeleteWorkSchedulesMutation,
  useGetEmployeesWorkSchedulesByUserIdsMutation,
  useUpdateWorkSchedulesMutation,
} from '@app/api/WorkSchedule';
import type { ModelEmployeeWorkSchedules } from '@app/api/WorkSchedule';
import {
  DATA_UPDATED_SUCCESSFULLY,
  SUCCESS,
  WARNING,
} from '@app/constants/alerts';
import {
  useMemoizeObject,
  useUserPrint,
  useNewUserAccessRights,
} from '@app/hooks';
import { NotificationContext } from '@app/providers';
import { handleCatchError } from '@app/services';
import { selectAccessRights, useAppSelector } from '@app/store';
import type { NEmployees } from '@app/types/Employees';
import type { NRecords } from '@app/types/Records';
import type { NSchedule } from '@app/types/Schedule';
import type { NUsers } from '@app/types/Users';

import {
  getCellsCountToRender,
  getFirstTwoMonths,
  getScrollingMonth,
  handleAddingScheduleId,
  moveUserToNewMonth as _moveUserToNewMonth,
  showDevNotification,
  getDatesForFewMonths,
  adaptTimeRange,
} from '@pages/WorkSchedule/utils';

import {
  INITIAL_ADDITION_FILTERS,
  INITIAL_END_WORKING_TIME,
  INITIAL_START_WORKING_TIME,
} from '@pages/WorkSchedule/constants/filters';

import { defaultSelectedDays } from '@pages/WorkSchedule/data';
import { getInfoByMonth } from '@pages/WorkSchedule/utils/getInfoByMonth';
import type { NWorkSchedule } from '@pages/WorkSchedule/WorkSchedule.types';

import type { BaseOptionType } from 'antd/es/select';

const extractWorkSchedules = (
  data: ModelEmployeeWorkSchedules[]
): NSchedule.ModelWorkSchedule[] => {
  const result: NSchedule.ModelWorkSchedule[] = [];
  (data || []).forEach((item: ModelEmployeeWorkSchedules) => {
    return result.push(...(item.WorkSchedules ?? []));
  });
  return result;
};

export const useSchedule = () => {
  const accessRights = useAppSelector(selectAccessRights);
  const {
    userAccessRights: {
      WorkSchedules: { IsScheduleView },
    },
  } = useNewUserAccessRights();
  const [searchParams] = useSearchParams();
  const { userId, database, activeBranchId } = useUserPrint();
  const { showNotification } = useContext(NotificationContext);
  const [deleteWorkSchedules] = useDeleteWorkSchedulesMutation();
  const [updateWorkSchedules] = useUpdateWorkSchedulesMutation();
  const [getEmployeesWorkSchedulesByUserIds] =
    useGetEmployeesWorkSchedulesByUserIdsMutation();
  const [getEmployeesRecordsOnDates] = useGetEmployeesRecordsOnDatesMutation();
  const [getBranchEmployees] = useLazyGetBranchEmployeesQuery();
  const [allPositions, setAllPositions] =
    useState<NWorkSchedule.AllPositions>(null);
  const [selectedPositions, setSelectedPositions] = useState<BaseOptionType[]>(
    []
  );
  const [employeeIds, setEmployeeIds] = useState<Array<NUsers.User['id']>>([]);
  const [employees, setEmployees] = useState<NEmployees.ModelEmployee[]>([]);
  const [dateS] = useState(getInfoByMonth().dateS);
  const [initialSchedules, setInitialSchedules] = useState<
    NSchedule.ModelWorkSchedule[]
  >([]);

  const filters = useRef<NWorkSchedule.Filters>(INITIAL_ADDITION_FILTERS);

  const [isFilterByDays, setFilterByDays] = useState(true);
  const [selectedEmployees, setSelectedEmployees] = useState<
    NEmployees.ModelEmployee[]
  >([]);
  const [photosAndMiddleNames, setPhotosAndMiddleNames] = useState<
    {
      id: NEmployees.ModelEmployee['Id'];
      nameMiddle: null;
      photo: NEmployees.ModelEmployee['Photo'];
    }[]
  >([]);
  const [form] = Form.useForm<NWorkSchedule.FiltersFormData>();

  const startTime = useRef<BaseOptionType>(INITIAL_START_WORKING_TIME);
  const endTime = useRef<BaseOptionType>(INITIAL_END_WORKING_TIME);

  const [reloadToggler, setReloadToggler] = useState(false);
  const [daysCount, setDaysCount] = useState('2');
  const [spaceInDays, setSpaceInDays] = useState('2');
  const [selectedDays, setSelectedDays] =
    useState<NWorkSchedule.SelectedDays>(defaultSelectedDays);
  const [currentMonth, setCurrentMonth] = useState<NWorkSchedule.CurrentMonth>({
    title: getInfoByMonth().title.toUpperCase(),
    startDate: getInfoByMonth().dateS,
  });
  const [renderedMonths, setRenderedMonths] =
    useState<NWorkSchedule.RenderedMonths>(getFirstTwoMonths());
  const [monthTitleLoader, setMonthTitleLoader] = useState(false);
  const [initialRecords, setInitialRecords] = useState<
    NRecords.ModelRecordsOnDates[]
  >([]);
  const [positionsLoader, setPositionsLoader] = useState(false);
  const [cellsContainerLoader, setCellsContainerLoader] = useState(false);
  const branchId = Number(searchParams.get('branch-id'));
  const employeeId = Number(searchParams.get('employee-id'));
  const [initialEmployees, setInitialEmployees] = useState<
    NEmployees.ModelEmployee[] | null
  >(null);
  const [isModal, setModal] = useState(false);
  const [mode, setMode] = useState<NWorkSchedule.ScheduleMode>('edit');

  const [cells, setCells] = useState<NWorkSchedule.CellsMap>(new Map());
  const [changingCells, setChangingCells] = useState<NWorkSchedule.CellsMap>(
    new Map()
  );
  const [deletingCells, setDeletingCells] = useState<NWorkSchedule.CellsMap>(
    new Map()
  );

  useEffect(() => {
    const fetchData = async () => {
      if (userId && database && activeBranchId) {
        setPositionsLoader(true);

        try {
          const effectiveBranchId = branchId || activeBranchId;
          const data = await getBranchEmployees({
            branchId: effectiveBranchId,
            ActiveBranchId: effectiveBranchId,
            // График работы показывает всех пользователей филиала, а не
            // только исполнителей (us/11349).
            includeUsers: true,
            logs: { isLogged: true, userId, database },
          }).unwrap();
          setInitialEmployees(data);
        } catch (error) {
          showNotification(handleCatchError(error), WARNING);
        } finally {
          setPositionsLoader(false);
        }
      }
    };

    fetchData();
  }, [
    userId,
    database,
    activeBranchId,
    accessRights,
    branchId,
    showNotification,
    getBranchEmployees,
  ]);

  const memoInitialEmployees =
    useMemoizeObject(initialEmployees) || initialEmployees;
  const memoSelectedPositions =
    useMemoizeObject(selectedPositions) || selectedPositions;
  const memoRenderedMonths = useMemoizeObject(renderedMonths) || renderedMonths;
  const memoAccessRights = useMemoizeObject(accessRights) || accessRights;

  useEffect(() => {
    const fetchData = async () => {
      if (
        userId &&
        database &&
        activeBranchId &&
        memoInitialEmployees !== null
      ) {
        setCellsContainerLoader(true);

        try {
          const dataFilteredByEmployee = employeeId
            ? memoInitialEmployees.filter(({ Id }) => Id === employeeId)
            : memoInitialEmployees;

          let dataFilteredByPositions: NEmployees.ModelEmployee[] = [];
          const positions = memoSelectedPositions.map((el) =>
            el.value === -1 ? { value: null, name: 'Без должности' } : el
          );
          if (positions.length > 0) {
            memoInitialEmployees.forEach((employee) => {
              const { PositionId } = employee;
              return positions.forEach(({ value }) => {
                if (PositionId === value) {
                  dataFilteredByPositions.push(employee);
                }
              });
            });
          } else {
            dataFilteredByPositions = dataFilteredByEmployee;
          }

          const handledPositions = memoInitialEmployees
            .map(
              ({
                PositionId,
                Position,
              }: {
                PositionId: NEmployees.ModelEmployee['PositionId'];
                Position: NEmployees.ModelEmployee['Position'];
              }) => ({ Id: PositionId, Name: Position })
            )
            .filter(
              (item: { Id: number | null; Name: string | null }) =>
                item?.Id !== null
            )
            .filter(
              (
                value: { Id: number | null; Name: string | null },
                index: number,
                self
              ) =>
                index ===
                self.findIndex(
                  (t: { Id: number | null; Name: string | null }) =>
                    t.Id === value.Id && t.Name === value.Name
                )
            );
          handledPositions.push({ Id: -1, Name: 'Без должности' });
          setAllPositions(handledPositions as NWorkSchedule.AllPositions);

          setEmployeeIds((dataFilteredByPositions || []).map(({ Id }) => Id));
          setPhotosAndMiddleNames(
            (dataFilteredByPositions || []).map(({ Id, Photo = null }) => {
              return {
                id: Id,
                nameMiddle: null,
                photo: Photo,
              };
            })
          );

          const _employees = dataFilteredByPositions || [];

          const effectiveBranchId = branchId || activeBranchId;

          const data = await getEmployeesWorkSchedulesByUserIds({
            ActiveBranchId: effectiveBranchId,
            branchId: effectiveBranchId,
            dateS: getInfoByMonth(memoRenderedMonths[0]).dateS,
            dateE: getInfoByMonth(
              memoRenderedMonths[memoRenderedMonths.length - 1]
            ).dateE,
            data: _employees.map(({ Id }) => Id),
          }).unwrap();
          setInitialSchedules(extractWorkSchedules(data));

          const employeeRecords = await getEmployeesRecordsOnDates({
            ActiveBranchId: branchId || activeBranchId,
            data: employeeId ? [employeeId] : _employees.map(({ Id }) => Id),
            dateS: getInfoByMonth(memoRenderedMonths[0]).dateS,
            dateE: getInfoByMonth(
              memoRenderedMonths[memoRenderedMonths.length - 1]
            ).dateE,
          }).unwrap();
          setInitialRecords(employeeRecords);

          setEmployees(_employees);
          setSelectedEmployees(_employees);
        } catch (error) {
          showNotification(handleCatchError(error), WARNING);
        } finally {
          setCellsContainerLoader(false);
        }
      }
    };

    fetchData();
  }, [
    userId,
    database,
    activeBranchId,
    reloadToggler,
    memoInitialEmployees,
    memoAccessRights,
    employeeId,
    memoSelectedPositions,
    branchId,
    dateS,
    memoRenderedMonths,
    showNotification,
    getEmployeesWorkSchedulesByUserIds,
    getEmployeesRecordsOnDates,
  ]);

  useEffect(() => {
    const dates = getDatesForFewMonths(renderedMonths);
    const cells = new Map();
    selectedEmployees.forEach((employee) => {
      const datesMap = new Map();
      dates.forEach((date) => {
        datesMap.set(date, {
          schedule: undefined,
          time: '',
          records: 0,
        });
      });
      cells.set(employee.Id, datesMap);
    });
    if (cells?.size > 0 && initialSchedules?.length > 0) {
      initialSchedules.forEach((schedule) => {
        const [dayWithoutTime] = schedule.TimeS.split('T');
        const cell = cells?.get(schedule.UserId)?.get(dayWithoutTime);
        if (cell) {
          cell.time = adaptTimeRange(schedule.TimeS, schedule.TimeE);
          // RTK Query возвращает замороженные объекты из кэша, поэтому кладём
          // в ячейку копию — дальше расписание мутируется (IsRecords, время смены)
          cell.schedule = { ...schedule };
          cells.get(schedule.UserId).set(dayWithoutTime, cell);
        }
      });
    }
    if (cells?.size > 0 && initialRecords?.length > 0) {
      initialRecords.forEach((record) => {
        if (record.EmployeeId && record.RecordsOnDates) {
          for (const [key, value] of Object.entries(record.RecordsOnDates)) {
            const [dayWithoutTime] = key.split('T');
            const cell = cells?.get(record.EmployeeId)?.get(dayWithoutTime);
            if (cell && dayWithoutTime && Number(value) > 0) {
              cell.schedule && (cell.schedule.IsRecords = true);
              cell.records = Number(value);
              cells.get(record.EmployeeId).set(dayWithoutTime, cell);
            }
          }
        }
      });
    }
    setCells(cells);
  }, [selectedEmployees, renderedMonths, initialSchedules, initialRecords]);

  const saveSchedules = useCallback(async () => {
    let clearDeletingCells = false;
    let clearChangingCells = false;
    setCellsContainerLoader(true);
    if (deletingCells.size > 0) {
      const schedulesToDelete = Array.from(deletingCells, ([, datesMap]) =>
        Array.from(datesMap, ([, cell]) => cell.schedule.Id)
      ).flat();

      try {
        await deleteWorkSchedules({
          ActiveBranchId: activeBranchId,
          data: schedulesToDelete,
        }).unwrap();

        clearDeletingCells = true;
        setReloadToggler((status) => !status);
        showNotification('Записи успешно удалены.', SUCCESS);
      } catch (error) {
        showNotification(handleCatchError(error), WARNING);
      }
    }
    if (changingCells.size > 0) {
      try {
        const schedulesToChange = Array.from(changingCells, ([, datesMap]) =>
          Array.from(datesMap, ([, cell]) => ({
            ...cell.schedule,
            Id: handleAddingScheduleId(cell.schedule.Id),
          }))
        ).flat();

        await updateWorkSchedules({
          ActiveBranchId: branchId || activeBranchId,
          data: schedulesToChange,
        }).unwrap();
        clearChangingCells = true;
        setReloadToggler((status) => !status);
        showNotification(DATA_UPDATED_SUCCESSFULLY, SUCCESS);
      } catch (error) {
        showNotification(handleCatchError(error), WARNING);
      }
    }

    if (clearDeletingCells || clearChangingCells) {
      const saveEffectiveBranchId = branchId || activeBranchId;

      if (saveEffectiveBranchId && memoInitialEmployees) {
        const data = await getEmployeesWorkSchedulesByUserIds({
          ActiveBranchId: saveEffectiveBranchId,
          branchId: saveEffectiveBranchId,
          dateS: getInfoByMonth(memoRenderedMonths[0]).dateS,
          dateE: getInfoByMonth(
            memoRenderedMonths[memoRenderedMonths.length - 1]
          ).dateE,
          data: memoInitialEmployees.map(({ Id }) => Id),
        }).unwrap();

        setInitialSchedules(extractWorkSchedules(data));
      }
    }

    clearDeletingCells && setDeletingCells(new Map());
    clearChangingCells && setChangingCells(new Map());
    setCellsContainerLoader(false);
  }, [
    deletingCells,
    changingCells,
    branchId,
    activeBranchId,
    memoRenderedMonths,
    memoInitialEmployees,
    showNotification,
    deleteWorkSchedules,
    updateWorkSchedules,
    getEmployeesWorkSchedulesByUserIds,
  ]);

  const filterByEmployees = useCallback(
    (values: BaseOptionType[], employees: NEmployees.ModelEmployee[]) => {
      setSelectedPositions(values);

      const filteredEmployees: NEmployees.ModelEmployee[] = [];
      (employees || []).forEach((employee: NEmployees.ModelEmployee) => {
        const { PositionId } = employee;
        return (
          values.map((el) =>
            el.value === -1 ? { value: null, name: 'Без должности' } : el
          ) || []
        ).forEach((position: BaseOptionType) => {
          const { value } = position;
          if (PositionId === value) {
            return filteredEmployees.push(employee);
          }
        });
      });
      if (!(values.length > 0)) {
        return setSelectedEmployees(employees);
      } else {
        return setSelectedEmployees(filteredEmployees);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [employees, selectedPositions, selectedEmployees]
  );

  const cancelChanges = useCallback(() => {
    setChangingCells(new Map());
    setDeletingCells(new Map());
  }, []);

  const handleScroll = useCallback(
    (event: React.UIEvent<HTMLDivElement>) => {
      if (monthTitleLoader) {
        return;
      }

      const scrollingMonth = getScrollingMonth({
        event: { target: event.currentTarget },
        renderedMonths,
      });

      if (scrollingMonth?.startDate) {
        setCurrentMonth(scrollingMonth);
      }
    },
    [monthTitleLoader, renderedMonths]
  );

  const cellsPerRowCount = useMemo(() => {
    return getCellsCountToRender(renderedMonths);
  }, [renderedMonths]);

  const moveUserToNewMonth: (
    newMonthsArray: string[],
    direction: 'forward' | 'backward',
    isLastMonth?: boolean,
    initialSchedules?: NSchedule.ModelWorkSchedule[]
  ) => void = useCallback(
    (newMonthsArray, direction, isLastMonth) => {
      _moveUserToNewMonth({
        activeBranchId: branchId || activeBranchId,
        initialSchedules,
        allPositions,
        newMonthsArray,
        employees,
        currentMonth,
        renderedMonths,
        direction,
        isLastMonth,
        employeeId,
        setCurrentMonth,
        setMonthTitleLoader,
        setCellsContainerLoader,
        setRenderedMonths,
        setInitialSchedules,
        showNotification,
        extractWorkSchedules,
        setInitialRecords,
        getEmployeesWorkSchedulesByUserIds,
        getEmployeesRecordsOnDates,
      });
    },
    [
      branchId,
      activeBranchId,
      initialSchedules,
      allPositions,
      employees,
      currentMonth,
      renderedMonths,
      employeeId,
      showNotification,
      getEmployeesWorkSchedulesByUserIds,
      getEmployeesRecordsOnDates,
    ]
  );

  return {
    allPositions,
    selectedPositions,
    employeeIds,
    employees,
    filters,
    isFilterByDays,
    positionsLoader,
    selectedEmployees,
    photosAndMiddleNames,
    isModal,
    form,
    startTime,
    endTime,
    daysCount,
    spaceInDays,
    selectedDays,
    initialSchedules,
    currentMonth,
    renderedMonths,
    cellsPerRowCount,
    monthTitleLoader,
    cellsContainerLoader,
    initialRecords,
    mode,
    cells,
    changingCells,
    deletingCells,
    setRenderedMonths,
    handleScroll,
    cancelChanges,
    setSelectedDays,
    setSpaceInDays,
    setDaysCount,
    setModal,
    setFilterByDays,
    setSelectedPositions,
    showDevNotification: () => showDevNotification(showNotification),
    saveSchedules,
    filterByEmployees,
    moveUserToLastMonth: moveUserToNewMonth,
    moveUserToNewMonth,
    setMode,
    setChangingCells,
    setDeletingCells,
    IsScheduleView,
  };
};
